import React from 'react';
import { Tv, Star, Users, Brain, Award, MessageSquare } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';

export const Home: React.FC = () => {
  const { t, language } = useLanguage();

  const features = {
    en: [
      {
        icon: <Users className="w-8 h-8" />,
        title: 'Characters',
        description: 'Explore the personality traits and growth of each character.',
        link: '/characters'
      },
      {
        icon: <Star className="w-8 h-8" />,
        title: 'Quotes',
        description: 'Relive memorable moments through the most classic and interesting quotes.',
        link: '/quotes'
      },
      {
        icon: <Brain className="w-8 h-8" />,
        title: 'Quiz',
        description: 'Find out which character you resemble most and discover interesting personality matches.',
        link: '/quiz'
      }
    ],
    zh: [
      {
        icon: <Users className="w-8 h-8" />,
        title: '角色介绍',
        description: '深入了解每个角色的性格特点和成长历程。',
        link: '/characters'
      },
      {
        icon: <Star className="w-8 h-8" />,
        title: '经典台词',
        description: '收录剧中最经典、最有趣的台词，重温那些难忘的时刻。',
        link: '/quotes'
      },
      {
        icon: <Brain className="w-8 h-8" />,
        title: '趣味测试',
        description: '测试你最像哪个角色，发现有趣的性格共鸣。',
        link: '/quiz'
      }
    ],
    ru: [
      {
        icon: <Users className="w-8 h-8" />,
        title: 'Персонажи',
        description: 'Узнайте о характерах и развитии каждого персонажа.',
        link: '/characters'
      },
      {
        icon: <Star className="w-8 h-8" />,
        title: 'Цитаты',
        description: 'Вспомните памятные моменты через самые классические и интересные цитаты.',
        link: '/quotes'
      },
      {
        icon: <Brain className="w-8 h-8" />,
        title: 'Тест',
        description: 'Узнайте, на какого персонажа вы больше всего похожи.',
        link: '/quiz'
      }
    ],
    fr: [
      {
        icon: <Users className="w-8 h-8" />,
        title: 'Personnages',
        description: 'Découvrez les traits de personnalité et l\'évolution de chaque personnage.',
        link: '/characters'
      },
      {
        icon: <Star className="w-8 h-8" />,
        title: 'Citations',
        description: 'Revivez les moments mémorables à travers les citations les plus classiques.',
        link: '/quotes'
      },
      {
        icon: <Brain className="w-8 h-8" />,
        title: 'Quiz',
        description: 'Découvrez à quel personnage vous ressemblez le plus.',
        link: '/quiz'
      }
    ],
    de: [
      {
        icon: <Users className="w-8 h-8" />,
        title: 'Charaktere',
        description: 'Entdecken Sie die Persönlichkeitsmerkmale und Entwicklung jeder Figur.',
        link: '/characters'
      },
      {
        icon: <Star className="w-8 h-8" />,
        title: 'Zitate',
        description: 'Erleben Sie denkwürdige Momente durch die klassischsten Zitate.',
        link: '/quotes'
      },
      {
        icon: <Brain className="w-8 h-8" />,
        title: 'Quiz',
        description: 'Finden Sie heraus, welchem Charakter Sie am ähnlichsten sind.',
        link: '/quiz'
      }
    ],
    nl: [
      {
        icon: <Users className="w-8 h-8" />,
        title: 'Personages',
        description: 'Ontdek de persoonlijkheidskenmerken en ontwikkeling van elk personage.',
        link: '/characters'
      },
      {
        icon: <Star className="w-8 h-8" />,
        title: 'Citaten',
        description: 'Herbeleef memorabele momenten door de meest klassieke citaten.',
        link: '/quotes'
      },
      {
        icon: <Brain className="w-8 h-8" />,
        title: 'Quiz',
        description: 'Ontdek op welk personage je het meest lijkt.',
        link: '/quiz'
      }
    ],
    ms: [
      {
        icon: <Users className="w-8 h-8" />,
        title: 'Watak-watak',
        description: 'Terokai ciri-ciri personaliti dan perkembangan setiap watak.',
        link: '/characters'
      },
      {
        icon: <Star className="w-8 h-8" />,
        title: 'Petikan',
        description: 'Hayati semula saat-saat yang tidak dilupakan melalui petikan klasik.',
        link: '/quotes'
      },
      {
        icon: <Brain className="w-8 h-8" />,
        title: 'Ujian',
        description: 'Ketahui watak mana yang paling mirip dengan anda.',
        link: '/quiz'
      }
    ]
  };

  const showFeatures = {
    en: {
      features: [
        "Unique fourth wall breaks with Malcolm directly addressing the audience",
        "Authentic portrayal of middle-class family life",
        "Distinct personalities and character development for each role",
        "Thoughtful and humane storylines"
      ]
    },
    zh: {
      features: [
        "独特的第四面墙打破手法，Malcolm经常与观众直接对话",
        "真实展现中产阶级家庭的生活状态",
        "每个角色都有鲜明的个性和成长轨迹",
        "充满智慧和人性的剧情设计"
      ]
    },
    ru: {
      features: [
        "Уникальные обращения Малкольма к зрителям",
        "Аутентичное изображение жизни семьи среднего класса",
        "Яркие личности и развитие каждого персонажа",
        "Продуманные и человечные сюжетные линии"
      ]
    },
    fr: {
      features: [
        "Malcolm s'adresse directement aux spectateurs de manière unique",
        "Portrait authentique de la vie d'une famille de classe moyenne",
        "Personnalités distinctes et développement pour chaque rôle",
        "Scénarios réfléchis et humains"
      ]
    },
    de: {
      features: [
        "Einzigartige Durchbrechung der vierten Wand durch Malcolm",
        "Authentische Darstellung des Mittelklasse-Familienlebens",
        "Ausgeprägte Persönlichkeiten und Entwicklung jeder Rolle",
        "Durchdachte und menschliche Handlungsstränge"
      ]
    },
    nl: {
      features: [
        "Unieke doorbreking van de vierde wand door Malcolm",
        "Authentieke weergave van het leven van een middenklasse gezin",
        "Duidelijke persoonlijkheden en ontwikkeling voor elke rol",
        "Doordachte en menselijke verhaallijnen"
      ]
    },
    ms: {
      features: [
        "Malcolm bercakap terus dengan penonton secara unik",
        "Gambaran autentik kehidupan keluarga kelas menengah",
        "Personaliti tersendiri dan perkembangan setiap watak",
        "Jalan cerita yang bijak dan berperikemanusiaan"
      ]
    }
  };

  return (
    <div className="space-y-8">
      <div className="bg-blue-600 text-white p-8 rounded-lg text-center">
        <h1 className="text-4xl font-bold mb-4">{t('home.welcome')}</h1>
        <p className="text-xl">{t('home.welcomeDesc')}</p>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold mb-4">{t('home.about')}</h2>
        <div className="space-y-6">
          <div className="text-gray-600 leading-relaxed">
            {t('home.aboutText')}
          </div>
          
          <div className="mt-6">
            <h3 className="text-xl font-bold mb-3">{t('home.features')}:</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              {showFeatures[language].features.map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ul>
          </div>

          <div className="mt-6">
            <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
              <Award className="w-6 h-6 text-yellow-500" />
              {t('home.awards')}
            </h3>
            <p className="text-gray-600 leading-relaxed">
              {t('home.awardsText')}
            </p>
          </div>
        </div>
      </div>

      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">
              {t('nav.features')}
            </h2>
            <p className="mt-4 text-xl text-gray-600">
              {t('nav.explore')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features[language].map((feature, index) => (
              <Link
                key={index}
                to={feature.link}
                className="block bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow"
              >
                <div className="text-blue-600 mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}; 